import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  Card,
  CardContent,
} from "@mui/material";
import { motion } from "framer-motion";
import CodeIcon from "@mui/icons-material/Code";
import BrushIcon from "@mui/icons-material/Brush";
import SpeedIcon from "@mui/icons-material/Speed";

const features = [
  {
    icon: <CodeIcon sx={{ fontSize: 40 }} />,
    title: "Schnell, sicher & zukunftssicher",
    description:
      "Ich setze auf performanten Code, schnelle Ladezeiten und neueste Technologien. Deine Website läuft stabil, sicher und ist bereit für kommende Anforderungen.",
  },
  {
    icon: <BrushIcon sx={{ fontSize: 40 }} />,
    title: "Individuelle Entwicklung statt Baukasten-Look",
    description:
      "Jede Website wird individuell an dein Unternehmen angepasst. Modern, nutzerfreundlich und auf dein Branding abgestimmt – damit du dich von der Konkurrenz abhebst.",
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    title: "Mehr Sichtbarkeit & Kunden",
    description:
      "Ich entwickle SEO-optimierte Webseiten, die bei Google besser gefunden werden – so erreichst du genau deine Zielgruppe und steigerst deine Reichweite nachhaltig.",
  },
];

const AboutSection: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor:
          theme.palette.mode === "dark" ? "background.default" : "grey.50",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={8} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 700,
                  mb: 2,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                Über pyzon
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ mb: 4, lineHeight: 1.6 }}
              >
                Seit 2018 arbeite ich als selbstständiger Webentwickler und
                Entwickler mit dem Fokus auf maßgeschneiderte, moderne
                Weblösungen.
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  mb: 4,
                  lineHeight: 1.8,
                  fontSize: "1.1rem",
                }}
              >
                Mit Leidenschaft für Technik, Entwicklung und Benutzerfreundlichkeit
                unterstütze ich Unternehmen dabei, ihre Online-Präsenz gezielt
                zu verbessern. Mein Ziel ist es, nicht nur optisch ansprechende,
                sondern auch performante, wartbare und skalierbare Webseiten zu
                schaffen – damit dein Unternehmen online erfolgreich
                durchstartet.
              </Typography>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {features.map((feature, index) => (
                <Grid item xs={12} key={feature.title}>
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.2 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        background:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(255, 255, 255, 0.8)",
                        backdropFilter: "blur(10px)",
                        border: `1px solid ${theme.palette.divider}`,
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                          transform: "translateX(8px)",
                          borderColor: theme.palette.primary.main,
                          "& .feature-icon": {
                            color: theme.palette.primary.main,
                            transform: "scale(1.1)",
                          },
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 3,
                          p: 3,
                        }}
                      >
                        <Box
                          className="feature-icon"
                          sx={{
                            color: "text.primary",
                            transition: "all 0.3s ease-in-out",
                            p: 1.5,
                            borderRadius: "12px",
                            background:
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.05)"
                                : "rgba(0, 0, 0, 0.03)",
                          }}
                        >
                          {feature.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              fontSize: "1.25rem",
                            }}
                          >
                            {feature.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              lineHeight: 1.6,
                              fontSize: "0.95rem",
                            }}
                          >
                            {feature.description}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AboutSection;
